import type { FC } from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import type { IServiceError } from "@/Components/Api/CommonServiceOutput";
import { sendTelemetryEvent } from "@/lib/telemetry";
import ButtonV2 from "@Components/Button/ButtonV2";
import useAjax, { AjaxMethod } from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import { Modal } from "@Components/Modal";
import { CommonEventType, MgmEventType, TelemetryEntity } from "@Components/Modules/Helpers";
import { TextboxAppearanceV2, TextboxV2 } from "@Components/Textbox";
import { mgmServicesCodeKey } from "@Constants/PortalConstants";
import { Default } from "@Routes/MemberGetMemberLinks";

import * as css from "./MgmDashboard.module.css";

export interface IMgmUpdateCodeModal {
    code: string;
    updateCode: (newCode: string) => void;
    modalClose: () => void;
    resources: { [key: string]: string };
}

type FormValues = {
    [mgmServicesCodeKey]: string;
};

const MgmUpdateCodeModal: FC<IMgmUpdateCodeModal> = ({ code, updateCode, modalClose, resources }) => {
    const { register, formState, handleSubmit, getValues, setError } = useForm<FormValues>({
        defaultValues: {
            [mgmServicesCodeKey]: code,
        },
        mode: "onChange",
    });

    useEffect(() => {
        sendTelemetryEvent({
            eventName: `${TelemetryEntity.Cashback}-${MgmEventType.dashboardReferralCodeEdit}`,
            actionType: "View",
        });
    }, []);

    const { request: updateCodeRequest, isLoading } = useAjax<IServiceError>({
        ajaxAction: Default.url,
        ajaxMethod: AjaxMethod.Post,
        success: ({ commonServiceOutput }) => {
            if (commonServiceOutput?.resultStatus?.success) {
                const newCode = getValues(mgmServicesCodeKey).toUpperCase();
                updateCode(newCode);
            } else {
                setError(mgmServicesCodeKey, {
                    message: commonServiceOutput?.resultStatus?.errorMessage,
                });
            }
        },
    });

    return (
        <Modal
            className={css.updateMgmCodeModal}
            allowBodyScroll={false}
            onClose={modalClose}
            isOpen
            closeOnEsc
        >
            <form
                onSubmit={handleSubmit(
                    (formRequest: FormValues) => {
                        updateCodeRequest({ action: "UserCodeUpdate" }, formRequest);
                    },
                    () => {
                        if (getValues(mgmServicesCodeKey).toUpperCase() === code) {
                            modalClose();
                        }
                    },
                )}
            >
                <p>{resources.title}</p>
                <p>{resources.description}</p>
                <p dangerouslySetInnerHTML={{ __html: resources.descriptionWarning }} />

                <TextboxV2
                    {...register(mgmServicesCodeKey, {
                        required: resources.errorGeneric,
                        pattern: { value: /^[a-z0-9]{3,12}$/gi, message: resources.errorGeneric },
                        validate: (insertedCode: string) => insertedCode !== code,
                    })}
                    appearance={TextboxAppearanceV2.Standard}
                    placeholder={resources.inputPlaceholder}
                    errorMessage={formState.errors[mgmServicesCodeKey]?.message}
                />

                <ButtonV2 appearance="transparentRoundMagentaBorderAppearance" onClick={modalClose}>
                    {resources.btnBack}
                </ButtonV2>

                <ButtonV2
                    appearance="magentaRoundAppearance"
                    type="submit"
                    onClick={() =>
                        sendTelemetryEvent({
                            eventName: `${TelemetryEntity.Common}-${CommonEventType.save}`,
                            actionType: "Click",
                        })
                    }
                >
                    {resources.btnUpdate}
                </ButtonV2>

                {isLoading && <Loading isModal />}
            </form>
        </Modal>
    );
};

export default MgmUpdateCodeModal;
